import React from 'react';
import { FaApple } from '@react-icons/all-files/fa/FaApple';
import { FaNpm } from '@react-icons/all-files/fa/FaNpm';
import { FaPython } from '@react-icons/all-files/fa/FaPython';
import { FaTerminal } from '@react-icons/all-files/fa/FaTerminal';
import { SiHomebrew } from '@react-icons/all-files/si/SiHomebrew';
import { SiGo } from '@react-icons/all-files/si/SiGo';
import { SiRust } from '@react-icons/all-files/si/SiRust';
import { VscExtensions } from '@react-icons/all-files/vsc/VscExtensions';
import { FiPackage } from '@react-icons/all-files/fi/FiPackage';
import { SourceTone } from 'Components/atoms/SourcePill/SourcePill';

export interface SourceMeta {
    label: string;
    tone: SourceTone;
    icon: React.ReactNode;
}

const ICON_SIZE = 13;

/**
 * Friendly label + icon + tone for each stored inventory `category` / `source`
 * value (the agent taxonomy — APD-1755). Keyed by the raw stored value; keep in
 * sync with `SOURCE_OPTIONS` in InventoryGrid and the ingest ecosystem set
 * (see APD-1756). A Map keeps the snake_case keys as string literals.
 */
const META = new Map<string, SourceMeta>([
    ['macos_application', { label: 'Application', tone: 'app', icon: <FaApple size={ICON_SIZE} /> }],
    ['macos_app', { label: 'Application', tone: 'app', icon: <FaApple size={ICON_SIZE} /> }],
    ['homebrew_formula', { label: 'Homebrew', tone: 'brew', icon: <SiHomebrew size={ICON_SIZE} /> }],
    ['homebrew_cask', { label: 'Homebrew cask', tone: 'brew', icon: <SiHomebrew size={ICON_SIZE} /> }],
    ['npm_package', { label: 'npm', tone: 'npm', icon: <FaNpm size={ICON_SIZE} /> }],
    ['pnpm_global_package', { label: 'pnpm', tone: 'npm', icon: <FaNpm size={ICON_SIZE} /> }],
    ['vscode_extension', { label: 'VS Code', tone: 'editor', icon: <VscExtensions size={ICON_SIZE} /> }],
    ['cursor_extension', { label: 'Cursor', tone: 'editor', icon: <VscExtensions size={ICON_SIZE} /> }],
    ['pip_package', { label: 'pip', tone: 'py', icon: <FaPython size={ICON_SIZE} /> }],
    ['go_module', { label: 'Go', tone: 'go', icon: <SiGo size={ICON_SIZE} /> }],
    ['cargo_crate', { label: 'Cargo', tone: 'rust', icon: <SiRust size={ICON_SIZE} /> }],
    ['managed_binary', { label: 'Binary', tone: 'binary', icon: <FiPackage size={ICON_SIZE} /> }],
    ['mcp_server', { label: 'Local MCP', tone: 'mcp', icon: <FaTerminal size={ICON_SIZE} /> }],
]);

const humanize = (raw: string): string => raw.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export const sourceMeta = (raw: string | null | undefined): SourceMeta => {
    const entry = META.get((raw ?? '').toLowerCase());
    if (entry) {
        return entry;
    }
    return {
        label: raw ? humanize(raw) : 'Unknown',
        tone: 'default',
        icon: <FiPackage size={ICON_SIZE} />,
    };
};
